import { CheckCircleSharp, ErrorSharp } from "@mui/icons-material";
import { Card, Typography } from "@mui/material";
import { memo, useMemo } from "react";

interface QCData {
  waiting_qc_call?: number;
  qc_called?: number;
}

interface DashboardData {
  today?: QCData;
  today_qty?: QCData;
  all_time?: QCData;
  all_time_qty?: QCData;
  tomorrow?: QCData;
  tomorrow_qty?: QCData;
  qc_complete_count_today?: number;
  qc_complete_count_today_qty?: number;
  qc_reject_count_today?: number;
  qc_reject_count_today_qty?: number;
}

interface ComponentProps {
  isTask: boolean;
  data: DashboardData;
}

// Shared card style
const cardStyle = {
  border: "1px solid #ccc",
  borderRadius: "4px",
  padding: "16px",
};

// Helper functions for safe data access
const getQCCount = (data: QCData | undefined, field: keyof QCData): number => {
  return data?.[field] || 0;
};

// Stat card component to reduce duplication
interface StatCardProps {
  label: string;
  value: number;
  textColor?: string;
  valueColor?: string;
  colSpan?: string;
}

const StatCard = memo(
  ({
    label,
    value,
    textColor = "text-gray-400",
    valueColor = "",
    colSpan = "col-span-1",
  }: StatCardProps) => (
    <div className={colSpan} style={cardStyle}>
      <div className="flex flex-col gap-1">
        <div className={`text-sm font-bold ${textColor}`}>{label}</div>
        <div className={`text-3xl font-bold ${valueColor}`}>{value}</div>
      </div>
    </div>
  ),
);

StatCard.displayName = "StatCard";

// QC Status card component for passed/rejected
interface QCStatusCardProps {
  icon: ReturnType<typeof CheckCircleSharp>;
  label: string;
  value: number;
  bgColor: string;
  textColor: string;
  valueColor: string;
}

const QCStatusCard = memo(
  ({
    icon,
    label,
    value,
    bgColor,
    textColor,
    valueColor,
  }: QCStatusCardProps) => (
    <div className="col-span-full" style={cardStyle}>
      <div className="flex gap-4 items-center">
        <div
          className={`w-[64px] h-[64px] rounded-lg ${bgColor} flex items-center justify-center`}
        >
          {icon}
        </div>
        <div className="flex flex-col gap-2">
          <div className={`${textColor} font-bold text-sm`}>{label}</div>
          <div className={`${valueColor} text-3xl font-bold`}>{value}</div>
        </div>
      </div>
    </div>
  ),
);

QCStatusCard.displayName = "QCStatusCard";

const ToQCAnswer = memo(({ isTask, data }: ComponentProps) => {
  const calculatedData = useMemo(() => {
    const todayData = isTask ? data?.today : data?.today_qty;
    const allTimeData = isTask ? data?.all_time : data?.all_time_qty;
    const tomorrowData = isTask ? data?.tomorrow : data?.tomorrow_qty;

    // Today's counts
    const todayPendingQC = getQCCount(todayData, "waiting_qc_call");
    const todayCall = getQCCount(todayData, "qc_called");

    // Tomorrow's counts
    const tomorrowPendingQC = getQCCount(tomorrowData, "waiting_qc_call");
    const tomorrowCall = getQCCount(tomorrowData, "qc_called");

    // Yesterday's counts (all_time - today - tomorrow)
    const yesterdayPendingQC = Math.max(
      0,
      getQCCount(allTimeData, "waiting_qc_call") -
        todayPendingQC -
        tomorrowPendingQC,
    );
    const yesterdayCall = Math.max(
      0,
      getQCCount(allTimeData, "qc_called") - todayCall - tomorrowCall,
    );

    // QC completion counts
    const qcPassed = isTask
      ? data?.qc_complete_count_today || 0
      : data?.qc_complete_count_today_qty || 0;
    const qcRejected = isTask
      ? data?.qc_reject_count_today || 0
      : data?.qc_reject_count_today_qty || 0;

    return {
      todayPendingQC,
      todayCall,
      yesterdayPendingQC,
      yesterdayCall,
      qcPassed,
      qcRejected,
    };
  }, [isTask, data]);

  return (
    <Card className="p-4 mb-4">
      <Typography variant="body1" className="font-semibold mb-2">
        To-QC/Answer
      </Typography>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <StatCard
          label="PENDING QC TODAY"
          value={calculatedData.todayPendingQC}
        />
        <StatCard
          label="PENDING QC YESTERDAY"
          value={calculatedData.yesterdayPendingQC}
          textColor="text-error"
          valueColor="text-error-dark"
        />
        <StatCard label="CALL TODAY" value={calculatedData.todayCall} />
        <StatCard
          label="CALL YESTERDAY"
          value={calculatedData.yesterdayCall}
          textColor="text-error"
          valueColor="text-error-dark"
        />
        <QCStatusCard
          icon={<CheckCircleSharp className="text-white w-8 h-8" />}
          label="QC PASSED"
          value={calculatedData.qcPassed}
          bgColor="bg-success"
          textColor="text-success"
          valueColor="text-success"
        />
        <QCStatusCard
          icon={<ErrorSharp className="text-white w-8 h-8" />}
          label="QC REJECTED"
          value={calculatedData.qcRejected}
          bgColor="bg-error"
          textColor="text-error"
          valueColor="text-error-dark"
        />
      </div>
    </Card>
  );
});

ToQCAnswer.displayName = "ToQCAnswer";

export default ToQCAnswer;
